import { useRef, useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import VirtualList from '@/common/base/virtual-scroll-list';
import type { VirtualListInstance } from '@/common/base/virtual-scroll-list';
import ReplyAgentBanner from './reply-agent-banner';
import EmailMessageItem from './email-message-item';
import FactoryMessageItem from './factory-message-item';
import type { MessageListItemViewModel } from './index';

// ─── ItemWrapper ──────────────────────────────────────────────────────────────

const ItemWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="w-full px-4">{children}</div>;
};

// ─── Props ────────────────────────────────────────────────────────────────────

export type ContactListViewProps = {
  viewModels: MessageListItemViewModel[];
  activeContactId: string | null;
  unreadStatusMap: Record<string, number>;
  scrollOffset: number;
  virtualListRef: React.RefObject<VirtualListInstance>;
  onNavigate: (contactId: string) => void;
  onBottomReached: () => void;
  onScrollOffsetChange: (offset: number) => void;
  onTogglePin?: (contactId: string, isPinned: boolean) => Promise<void>;
};

// ─── ContactListView ──────────────────────────────────────────────────────────

export default function ContactListView({
  viewModels,
  activeContactId,
  unreadStatusMap,
  scrollOffset,
  virtualListRef,
  onNavigate,
  onBottomReached,
  onScrollOffsetChange,
  onTogglePin,
}: ContactListViewProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const getScrollElement = useMemoizedFn(() => listRef.current as HTMLElement);

  const estimateSize = useMemoizedFn((index: number) => {
    const item = viewModels[index];
    if (!item) return 60;
    if (item.type === 'FACTORY_GZH') return 75;
    return item.isAiManaged ? 85 : 60;
  });

  // isAiManaged 变化会影响 estimateSize 返回值，需要通知 virtualizer 重新测量高度
  const aiManagedKey = viewModels.map(vm => (vm.isAiManaged ? '1' : '0')).join('');
  useEffect(() => {
    virtualListRef.current?.measure();
  }, [aiManagedKey, virtualListRef]);

  if (viewModels.length === 0) {
    return (
      <div className="flex flex-col h-full relative">
        <ReplyAgentBanner />
        <div className="flex-1 flex items-center justify-center">
          <span className="text-sm text-[#A3A3A3]">No messages yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative">
      <ReplyAgentBanner />
      <div ref={listRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        <VirtualList
          ref={virtualListRef}
          data={viewModels}
          initialOffset={scrollOffset}
          setScrollOffset={onScrollOffsetChange}
          estimateSize={estimateSize}
          gap={8}
          classNames={{
            scrollRoot: 'py-4',
          }}
          getScrollElement={getScrollElement}
          onBottomReached={onBottomReached}
          renderItem={(item: MessageListItemViewModel) => {
            const unreadCount = unreadStatusMap[item.id] ?? 0;
            const isSelected = item.id === activeContactId;

            if (item.type === 'FACTORY_GZH') {
              return (
                <ItemWrapper key={item.id}>
                  <FactoryMessageItem
                    key={item.id}
                    data={item}
                    unreadCount={unreadCount}
                    isSelected={isSelected}
                    onNavigate={onNavigate}
                  />
                </ItemWrapper>
              );
            }
            return (
              <ItemWrapper key={item.id}>
                <EmailMessageItem
                  key={item.id}
                  data={item}
                  unreadCount={unreadCount}
                  isSelected={isSelected}
                  onNavigate={onNavigate}
                  onTogglePin={onTogglePin}
                />
              </ItemWrapper>
            );
          }}
        />
      </div>
    </div>
  );
}
